import { motion } from 'framer-motion';
import { User, Calendar, Euro, ArrowUpRight } from 'lucide-react';

interface RecentSale {
  id: string;
  amount: number;
  formattedAmount: string;
  date: string;
  formattedDate: string;
  clientName: string;
  clientId: string;
  notes?: string;
}

interface RecentSalesTableProps {
  sales: RecentSale[];
}

export function RecentSalesTable({ sales }: RecentSalesTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800">
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              Cliente
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              Monto
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              Fecha
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              Estado
            </th>
          </tr>
        </thead>
        <tbody>
          {sales.map((sale, index) => (
            <motion.tr
              key={sale.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ backgroundColor: 'hsl(var(--accent))' }}
              className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <td className="py-4 px-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    {sale.clientName.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {sale.clientName}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <User className="w-3 h-3 inline mr-1" />
                      Cliente
                    </p>
                  </div>
                </div>
              </td>
              <td className="py-4 px-4">
                <div className="flex items-center">
                  <Euro className="w-4 h-4 text-emerald-500 mr-2" />
                  <span className="font-bold text-gray-900 dark:text-white">
                    {sale.formattedAmount}
                  </span>
                </div>
                {sale.notes && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-[200px]">
                    {sale.notes}
                  </p>
                )}
              </td>
              <td className="py-4 px-4">
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span className="text-sm">{sale.formattedDate}</span>
                </div>
              </td>
              <td className="py-4 px-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  Completada
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>

      {sales.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Euro className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No hay ventas recientes
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Las ventas recientes aparecerán aquí
          </p>
        </div>
      )}
    </div>
  );
}
